/**
 * Category drill-down modal — the files behind one Storage bucket, grouped
 * by FOLDER (expandable), with checkbox selection and bulk move-to-Bin —
 * Google-Drive-style storage management.
 *
 * - Data: GET /orgs/:id/storage/files (owner/admin role — file names cross
 *   folder-permission boundaries, plan §4.3). A 403 renders as copy, not an
 *   error state: a plain org member can open the modal and learn why it's
 *   empty for them.
 * - Groups are folders (full VFS path), sorted by group size DESC; the
 *   largest group starts expanded, the rest collapsed. A group checkbox
 *   selects/clears its whole folder. The footer totals what's ticked.
 * - Deletion: DELETE /api/files/:id per selected file — the SAME
 *   move-to-bin every drive surface uses, permission-checked per file
 *   server-side. NOT a permanent delete; the bytes move to the Bin (still
 *   counted as drive storage) until the Bin is emptied — the confirm
 *   dialog says so. Partial failures keep their rows and are named.
 * - Rendered inside the settings tree but position:fixed, so it applies the
 *   theme class itself (modal-outside-ThemedArea rule, CLAUDE.md).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useTheme, getThemeClass, useConfirm, Tooltip } from '@papan/core-ui/ui'
import { filesApi } from '@papan/files/client'
import { storageApi, type CategoryFilesResponse, type CategoryFileRow } from '../../client/storage'
import { formatBytes } from '../budget/bits'
import { Checkbox, shortenPath, fmtDate, folderLabel } from './shared'
import { pinnedBatch } from '@papan/api-client'

/** Large-files-only floor (review: "only large files shown here") — fixed
 *  at 10 KB, applied in SQL, no UI to change it (filter pills were tried
 *  and removed at review). */
const MIN_BYTES = 10 * 1024

/**
 * Floors tried in order, stopping at the first that returns rows.
 *
 * ADAPTIVE because a dead-end modal reads as broken: the 1.35 kB Images
 * bucket showed "nothing" under a fixed floor even though the header
 * counted files.
 *
 * The middle rung is the one that earns its keep. Falling straight from
 * 10 KB to "everything" listed the Other bucket's 16 rows of which 13
 * were ZERO bytes — `.chain` pointers and empty `Untitled`s that can
 * free nothing, i.e. 81% noise on a surface whose entire job is
 * reclaiming space. `1` keeps files that hold at least one byte.
 *
 * The last rung exists so a bucket made ENTIRELY of empty files is still
 * browsable rather than a second dead end — the user asked for both
 * behaviours, and they only conflict if you pick one floor.
 */
const FLOOR_LADDER = [MIN_BYTES, 1, 0] as const

interface FolderGroup {
  folderId: string | null
  /** Display path — VFS root stripped: 'papan/ttt' → '/ttt', root → '/'. */
  label: string
  files: CategoryFileRow[]
  bytes: number
}

export function CategoryFilesModal({
  tenantId,
  category,
  categoryLabel,
  onClose,
  onChanged,
  onOpenFile,
}: {
  tenantId: string
  category: string
  /** Already-translated bucket label — the modal title. */
  categoryLabel: string
  onClose: () => void
  /** Fired after successful deletions so the page refreshes its totals. */
  onChanged: () => void
  /** Shell-owned navigation to the file's folder with it revealed —
   *  turns the cleanup surface into a triage surface (followups §2.4). */
  onOpenFile?: (folderId: string | null, fileId: string) => void
}) {
  const { t } = useTranslation('core-ui')
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const confirm = useConfirm()

  const [data, setData] = useState<CategoryFilesResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [forbidden, setForbidden] = useState(false)
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  // null = "no user toggle yet" → default: largest group expanded.
  const [expanded, setExpanded] = useState<Set<string | null> | null>(null)

  // Stale-response guard: each load() invalidates every earlier in-flight
  // one (incl. the adaptive second fetch), and unmount invalidates all —
  // the same discipline as the page's `alive` flag, shaped for a callback
  // that fires from more than one place.
  const loadSeq = useRef(0)
  const load = useCallback(() => {
    const seq = ++loadSeq.current
    setLoading(true)
    storageApi.listCategoryFiles(tenantId, category, FLOOR_LADDER[0])
      .then(async (d) => {
        // Walk down the ladder only while the bucket is known non-empty —
        // see FLOOR_LADDER. Worst case (a bucket of nothing but empty
        // files) is three round-trips, and only on that bucket.
        for (let i = 1; i < FLOOR_LADDER.length && d.files.length === 0 && d.total > 0; i++) {
          d = await storageApi.listCategoryFiles(tenantId, category, FLOOR_LADDER[i])
        }
        if (seq !== loadSeq.current) return
        setData(d)
        setError(null)
        setForbidden(false)
      })
      .catch((e: any) => {
        if (seq !== loadSeq.current) return
        if (e?.status === 403) setForbidden(true)
        else setError(e?.message || 'Failed to load files')
      })
      .finally(() => { if (seq === loadSeq.current) setLoading(false) })
  }, [tenantId, category])

  useEffect(() => {
    load()
    return () => { loadSeq.current++ }
  }, [load])

  // Esc closes — the overlay click already does, keyboard parity.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Rows arrive size-DESC from the server (largest-first is the fixed
  // order for a free-up-space surface — sort controls were tried and
  // removed at review); grouping preserves that order inside each folder,
  // and the groups themselves order by their total.
  const groups = useMemo<FolderGroup[]>(() => {
    if (!data) return []
    const byFolder = new Map<string | null, FolderGroup>()
    for (const f of data.files) {
      const key = f.folderId ?? null
      let g = byFolder.get(key)
      if (!g) {
        const label = folderLabel(f.folder)
        g = { folderId: key, label, files: [], bytes: 0 }
        byFolder.set(key, g)
      }
      g.files.push(f)
      g.bytes += f.bytes
    }
    return [...byFolder.values()].sort((a, b) => (b.bytes - a.bytes) || a.label.localeCompare(b.label))
  }, [data])

  const expandedSet = expanded ?? new Set(groups.length ? [groups[0].folderId] : [])
  const toggleExpanded = (folderId: string | null) => {
    const next = new Set(expandedSet)
    if (next.has(folderId)) next.delete(folderId)
    else next.add(folderId)
    setExpanded(next)
  }

  // A row the caller can read but not write is selectable-looking but not
  // deletable; every selection path filters on it so a read-only row can
  // never end up in the delete batch (followups §3.3). `?? true` keeps a
  // pre-§3.3 API's rows working, where the listing was admin-only.
  const canDelete = (f: CategoryFileRow) => f.canDelete ?? true

  const toggleFile = (id: string) => {
    const file = data?.files.find((f) => f.id === id)
    if (file && !canDelete(file)) return
    setSelected((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  const toggleGroup = (g: FolderGroup) => {
    setSelected((s) => {
      const next = new Set(s)
      const targets = g.files.filter(canDelete)
      const allIn = targets.every((f) => next.has(f.id))
      for (const f of targets) {
        if (allIn) next.delete(f.id)
        else next.add(f.id)
      }
      return next
    })
  }

  const selectedFiles = useMemo(
    () => (data ? data.files.filter((f) => selected.has(f.id)) : []),
    [data, selected],
  )
  const selectedBytes = selectedFiles.reduce((s, f) => s + f.bytes, 0)

  const handleDeleteSelected = async () => {
    if (!selectedFiles.length) return
    const ok = await confirm({
      title: t('settings.storage.manage.deleteSelected'),
      message: t('settings.storage.manage.deleteSelectedConfirm', {
        count: selectedFiles.length,
        size: formatBytes(selectedBytes),
      }),
      type: 'danger',
      confirmText: t('common:delete'),
    })
    if (!ok) return

    setDeleting(true)
    // #1316 review round 2 (owner, 2026-10-28): the bulk delete the person confirmed FINISHES as the identity that
    // confirmed it — every delete goes as it, never as the next session; nothing is shown once that session ended
    const batch = pinnedBatch()
    const succeeded = new Set<string>()
    const failed: string[] = []
    // Sequential on purpose — each delete is a full move-to-bin write; a
    // parallel volley over N files races the tree writer for no user gain.
    for (const f of selectedFiles) {
      try {
        // Inline, not the delete job: these are single files, never a big tree (#921).
        await filesApi.deleteFile(f.id, batch.opts())
        succeeded.add(f.id)
      } catch {
        failed.push(f.name)
      }
    }
    if (batch.ended()) return
    const remaining = data!.files.filter((f) => !succeeded.has(f.id))
    const remainingTotal = data!.total - succeeded.size
    setData((d) => d && {
      ...d,
      total: remainingTotal,
      totalBytes: d.totalBytes - selectedFiles.filter((f) => succeeded.has(f.id)).reduce((s, f) => s + f.bytes, 0),
      files: remaining,
    })
    setSelected(new Set())
    setError(failed.length ? t('settings.storage.manage.deleteFailed', { names: failed.join(', ') }) : null)
    setDeleting(false)
    if (succeeded.size) onChanged()
    // Review fix: deleting every SHOWN row of a floored bucket must not
    // render "Nothing here" while sub-floor files remain — reload, which
    // re-runs the adaptive-floor logic and surfaces them.
    if (remaining.length === 0 && remainingTotal > 0) load()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 ws-anim-fade-in" onClick={onClose} />
      <div className={`relative z-10 max-w-lg w-full max-h-[80vh] flex flex-col ${themeClass} ws-glass-modal ws-anim-modal-in`}>
        <div className="flex items-baseline justify-between gap-3 px-5 pt-4 pb-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold themed-text truncate">{categoryLabel}</h3>
            {data && (
              <p className="themed-text-muted text-xs mt-0.5">
                {formatBytes(data.totalBytes)} · {t('settings.storage.fileCount', { count: data.total })}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label={t('common:close')}
            className="p-1 -m-1 themed-text-muted hover:themed-text transition-colors rounded"
          >
            ✕
          </button>
        </div>

        <div className="px-5 overflow-y-auto min-h-0 flex-1">
          {loading && <div className="py-6 text-sm themed-text-muted">{t('common:loading')}</div>}
          {forbidden && (
            <div className="py-6 text-sm themed-text-muted">{t('settings.storage.manage.noAccess')}</div>
          )}
          {error && <div className="py-2 text-sm text-red-400">{error}</div>}
          {/* "Nothing here" over a header counting 26 files is a lie — say
              which kind of empty this is. `total > 0` covers the member
              whose whole bucket was filtered away, and also a bucket that
              emptied between the rollup and the listing. */}
          {data && !loading && data.files.length === 0 && !forbidden && (
            <div className="py-6 text-sm themed-text-muted">
              {data.filtered || data.total > 0
                ? t('settings.storage.manage.noneVisible')
                : t('settings.storage.manage.empty')}
            </div>
          )}

          {groups.map((g) => {
            const isOpen = expandedSet.has(g.folderId)
            // The group control speaks for the rows it can actually act on:
            // a folder of read-only files offers nothing to tick.
            const groupTargets = g.files.filter(canDelete)
            const allSelected = groupTargets.length > 0 && groupTargets.every((f) => selected.has(f.id))
            const someSelected = !allSelected && groupTargets.some((f) => selected.has(f.id))
            return (
              <div key={g.folderId ?? '∅'} className="border-b themed-border-secondary last:border-b-0">
                <div className="flex items-center gap-2.5 py-2 text-sm">
                  {/* Same reasoning as the row control: a folder whose files
                      are all read-only disables its box, and silence there
                      reads as a bug. */}
                  <Tooltip
                    content={groupTargets.length === 0 ? t('settings.storage.manage.readOnlyFolder') : null}
                    always
                    placement="top"
                    className="inline-flex shrink-0"
                  >
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      disabled={groupTargets.length === 0}
                      onChange={() => toggleGroup(g)}
                      aria-label={g.label}
                    />
                  </Tooltip>
                  <button
                    onClick={() => toggleExpanded(g.folderId)}
                    className="flex items-center gap-1.5 flex-1 min-w-0 text-left group"
                    aria-expanded={isOpen}
                  >
                    <span className="themed-text-muted text-[10px] w-3 shrink-0">{isOpen ? '▾' : '▸'}</span>
                    <span className="themed-text font-medium truncate group-hover:underline underline-offset-2" title={g.label}>
                      {shortenPath(g.label)}
                    </span>
                  </button>
                  <span className="themed-text tabular-nums text-xs shrink-0">{formatBytes(g.bytes)}</span>
                  <span className="themed-text-muted text-xs tabular-nums shrink-0">
                    {t('settings.storage.fileCount', { count: g.files.length })}
                  </span>
                </div>
                {isOpen && g.files.map((f) => (
                  <label key={f.id} className="flex items-center gap-2.5 py-1.5 pl-6 pr-1 text-sm cursor-pointer hover:themed-bg-tertiary rounded">
                    {/* A disabled control with no explanation is the thing
                        being complained about — say WHY on hover. `always`
                        because the trigger is a checkbox, not truncated
                        text; the tooltip portals out at z-[9999] so the
                        modal doesn't clip it. */}
                    <Tooltip
                      content={canDelete(f) ? null : t('settings.storage.manage.readOnly')}
                      always
                      placement="top"
                      className="inline-flex shrink-0"
                    >
                      <Checkbox
                        checked={selected.has(f.id)}
                        disabled={!canDelete(f)}
                        onChange={() => toggleFile(f.id)}
                      />
                    </Tooltip>
                    <span
                      className={`flex-1 min-w-0 truncate ${canDelete(f) ? 'themed-text' : 'themed-text-muted'}`}
                      title={f.name}
                    >
                      {f.name}
                    </span>
                    {/* Modified date — the disambiguator for look-alike rows
                        (conflict renames, "files (N items).zip" downloads). */}
                    <span className="themed-text-muted text-xs tabular-nums w-14 text-right shrink-0">{fmtDate(f.modifiedAt)}</span>
                    <span className="themed-text-muted text-xs tabular-nums w-16 text-right shrink-0">{formatBytes(f.bytes)}</span>
                    {onOpenFile && (
                      <button
                        // A label, not a span: stop the row-label's checkbox
                        // toggle from also firing on the navigate click.
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenFile(f.folderId, f.id) }}
                        className="themed-text-muted hover:themed-text text-xs shrink-0 px-0.5"
                        title={t('settings.storage.manage.openInDrive')}
                        aria-label={t('settings.storage.manage.openInDrive')}
                      >
                        ↗
                      </button>
                    )}
                  </label>
                ))}
              </div>
            )
          })}

        </div>

        {/* Selection footer — always visible so the running total has a
            fixed home; the button arms only when something is ticked. */}
        <div className="px-5 py-3 border-t themed-border-secondary">
        {/* This is a free-up-space surface, not a file browser: the list is
            the largest files only (10 KB floor, top 100). The header counts
            the WHOLE bucket, so without this the two disagree silently.
            Deliberately driven by the gap itself (bucket total vs listed
            rows) rather than by which floor was applied — that way a small
            bucket that fell back to showing everything says nothing, and
            the 100-row cap is covered by the same line. */}
        {/* Only when SOMETHING is listed — "N more aren't shown" reads as
            nonsense above an empty list, which the empty-state copy has
            already explained. */}
        {data && !loading && !forbidden && data.files.length > 0 && data.total > data.files.length && (
          <div className="themed-text-muted text-xs mb-2">
            {t('settings.storage.manage.listNote', { count: data.total - data.files.length })}
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <span className="themed-text-muted text-xs tabular-nums">
            {t('settings.storage.manage.selectedSummary', {
              count: selected.size,
              size: formatBytes(selectedBytes),
            })}
          </span>
          <button
            onClick={handleDeleteSelected}
            disabled={!selected.size || deleting}
            className="text-sm px-3 py-1.5 rounded-md bg-red-500/90 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {deleting ? t('common:loading') : t('settings.storage.manage.deleteSelected')}
          </button>
        </div>
        </div>
      </div>
    </div>
  )
}
